import { keyBy } from 'lodash/fp';

import { Case } from 'dashboard/models/case';
import { Device } from 'dashboard/models/device';
import { Document } from 'dashboard/models/document';
import { Environment } from 'dashboard/models/environment';
import { Inquiry } from 'dashboard/models/inquiry';
import { InquirySession } from 'dashboard/models/inquiry-session';
import { InquiryTemplate } from 'dashboard/models/inquiry-template';
import { InquiryShowViewInquiryTemplateVersion } from 'dashboard/models/inquiry-template-version';
import { List } from 'dashboard/models/list';
import { ListItem } from 'dashboard/models/list-item';
import { ListMatch } from 'dashboard/models/list-match';
import { Report } from 'dashboard/models/report';
import { ReportTemplate } from 'dashboard/models/report-template';
import { Selfie } from 'dashboard/models/selfie';
import { Tag } from 'dashboard/models/tag';
import { Template } from 'dashboard/models/template';
import { User } from 'dashboard/models/user';
import { Verification } from 'dashboard/models/verification';
import { Workflow } from 'dashboard/models/workflow';
import { WorkflowRun } from 'dashboard/models/workflow-run';
import { WorkflowRunStepAction } from 'dashboard/models/workflow-step';
import { WorkflowVersion } from 'dashboard/models/workflow-version';
import { InquiryActions, InquiryActionType } from 'dashboard/redux/actions/inquiry';

export interface InquiryState {
  cases: Case[];
  creator: User | undefined;
  devices: Device[];
  documents: Document[];
  environment: Environment | undefined;
  inquiry: Inquiry | undefined;
  inquiryTemplateVersion: InquiryShowViewInquiryTemplateVersion | undefined;
  listItems: Record<string, ListItem>;
  listMatches: Record<string, ListMatch>;
  lists: Record<string, List>;
  redactor: User | undefined;
  reportTemplates: ReportTemplate[];
  reports: Report[];
  reviewer: User | WorkflowRun | WorkflowRunStepAction | undefined;
  reviewerWorkflow: Workflow | undefined;
  reviewerWorkflowRun: WorkflowRun | undefined;
  reviewerWorkflowVersion: WorkflowVersion | undefined;
  selfies: Selfie[];
  sessions: Record<string, InquirySession>;
  tags: Tag[];
  template: InquiryTemplate | Template | undefined;
  verifications: Verification[];
}

export const initialState: InquiryState = {
  cases: [],
  devices: [],
  documents: [],
  environment: undefined,
  inquiry: undefined,
  inquiryTemplateVersion: undefined,
  lists: {},
  listItems: {},
  listMatches: {},
  creator: undefined,
  redactor: undefined,
  reviewer: undefined,
  reviewerWorkflow: undefined,
  reviewerWorkflowRun: undefined,
  reviewerWorkflowVersion: undefined,
  reports: [],
  reportTemplates: [],
  selfies: [],
  sessions: {},
  tags: [],
  template: undefined,
  verifications: [],
};

const inquiryReducer = (
  state: InquiryState = initialState,
  action: InquiryActions,
): InquiryState => {
  switch (action.type) {
    case InquiryActionType.ClearInquiry: {
      return initialState;
    }

    case InquiryActionType.SetInquiry: {
      return {
        ...state,
        inquiry: action.payload.inquiry,
      };
    }

    case InquiryActionType.SetInquiryRedactor: {
      return {
        ...state,
        redactor: action.payload.redactor,
      };
    }

    case InquiryActionType.SetInquiryDetails: {
      return {
        ...state,
        cases: action.payload.cases,
        creator: action.payload.creator,
        devices: action.payload.devices,
        documents: action.payload.documents,
        environment: action.payload.environment,
        inquiryTemplateVersion: action.payload.inquiryTemplateVersion,
        listItems: keyBy('id', action.payload.listItems),
        listMatches: keyBy('id', action.payload.listMatches),
        lists: keyBy('id', action.payload.lists),
        redactor: action.payload.redactor,
        reportTemplates: action.payload.reportTemplates,
        reports: action.payload.reports,
        reviewer: action.payload.reviewer,
        reviewerWorkflow: action.payload.reviewerWorkflow,
        reviewerWorkflowRun: action.payload.reviewerWorkflowRun,
        reviewerWorkflowVersion: action.payload.reviewerWorkflowVersion,
        selfies: action.payload.selfies,
        sessions: keyBy('id', action.payload.sessions),
        tags: action.payload.tags,
        template: action.payload.template,
        verifications: action.payload.verifications,
      };
    }

    case InquiryActionType.SetInquiryReports: {
      return {
        ...state,
        reports: action.payload.reports,
      };
    }

    default:
      return state;
  }
};

export default inquiryReducer;
